import { effect, inject, Injectable, OnDestroy } from "@angular/core";
import { asyncScheduler, Subject, Subscription, tap, throttleTime } from "rxjs";

import { isiFirmTax, isJobsBilling, isTaxprep } from "@app/views-interfaces/ApplicationType.enum";
import type { IFirmUser } from "@interfaces/index";
import { IBaseCard, ITranslateObject, JobsBillingCard } from "src/app/views-interfaces";
import { CardDisplayService } from "../../shared/card-display/card-display.service";
import { IDashboardModel } from "../../views-interfaces/dashboard-model.interface";
import { AnalyticsService } from "../analytics-service/analytics.service";
import { AuthService } from "../auth/auth.service";
import { CardViewService } from "../card-view-service/card-view.service";
import { DashboardsService } from "../dashboards-service/dashboards.service";
import { HelpersService } from "../helpers-service/helpers.service";
import { IFirmContactsService } from "../ifirm-contacts-service/ifirm-contacts.service";
import { IFirmGroupsService } from "../ifirm-groups-service/ifirm-groups.service";
import { IfirmTaxCardsService } from "../ifirm-tax-cards-service/ifirm-tax-cards.service";
import { JobsAndBillingCommonService } from "../jobs-and-billing-common-service/jobs-and-billing-common.service";

export interface IFirmUserWithId extends IFirmUser {
    Id: string;
}
@Injectable()
export class FiltersService implements OnDestroy {
    private readonly authService = inject(AuthService);
    private readonly dashboardsService = inject(DashboardsService);
    private readonly helpersService = inject(HelpersService);
    private readonly analyticsService = inject(AnalyticsService);
    private readonly ifirmTaxCardsService = inject(IfirmTaxCardsService);
    private readonly cardDisplayService = inject(CardDisplayService);
    private readonly iFirmContactsService = inject(IFirmContactsService);
    cardViewService = inject(CardViewService);
    iFirmGroupsService = inject(IFirmGroupsService);
    private readonly jobsAndBillingCommonService = inject(JobsAndBillingCommonService);

    private filtersChangedEmitter = new Subject<boolean>();
    public filtersChangedEvent$ = this.filtersChangedEmitter.asObservable();
    private readonly subscription = new Subscription();
    private ifirmContactAccessibleRefresh: ReturnType<typeof setInterval> | undefined;
    private accessibleSessionChangedSub: Subscription | null = null;
    private currentDashboard: IDashboardModel;
    private skipFilterChange = { Filtertype: "", value: false };
    private static readonly FILTER_ANALYTICS_EVENT = "Filters are changed";
    // Consolidates rapid-fire handleFilterChange bursts (e.g. cascading async effects) into leading and trailing emissions only.
    private static readonly FILTER_CHANGE_DEBOUNCE_MS = 100;
    private readonly filterChangeRequestEmitter = new Subject<boolean>();

    public activeFilters = new Map<string, (card: IBaseCard) => boolean>();
    public activeFiltersForPrint = new Map<string, { label: string | ITranslateObject; values: (string | ITranslateObject)[] }>();
    public activeSort: ((a: IBaseCard, b: IBaseCard) => number) | undefined;
    public activeSortForPrint: string | undefined;

    constructor() {
        this.resetActiveFiltersAndSort();
        this.refreshFiltersDependencies();
        this.subscription.add(
            this.helpersService.refreshFiltersEvent$.subscribe(async () => {
                await this.helpersService.initializeCategories();
            }),
        );

        this.subscription.add(
            this.dashboardsService.currentDashboardChanged$.subscribe(async () => {
                this.resetActiveFiltersAndSort();
                await this.refreshFiltersDependencies();
            }),
        );

        effect(() => {
            const currentAvailableDocuments = this.ifirmTaxCardsService.currentAvailableDocuments();
            if (!currentAvailableDocuments) return;

            this.filtersChangedEmitter.next(false);
        });

        this.subscription.add(
            this.filterChangeRequestEmitter
                .pipe(
                    tap((trackAnalytics) => {
                        if (trackAnalytics) this.trackFilterAnalytics();
                    }),
                    throttleTime(FiltersService.FILTER_CHANGE_DEBOUNCE_MS, asyncScheduler, { leading: true, trailing: true }),
                )
                .subscribe(() => this.filtersChangedEmitter.next(false)),
        );
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
        clearInterval(this.ifirmContactAccessibleRefresh);
    }

    private resetActiveFiltersAndSort(): void {
        this.activeFilters.clear();
        this.activeFiltersForPrint.clear();
        this.activeSort = undefined;
        this.activeSortForPrint = undefined;
    }

    async refreshFiltersDependencies() {
        this.currentDashboard = await this.dashboardsService.getCurrentDashboard();
        await this.helpersService.initializeCategories();
        await this.helpersService.mergeSteps();
        if (isJobsBilling(this.currentDashboard?.ApplicationType)) {
            this.ifirmContactAccessibleRefresh ??= setInterval(async () => {
                await this.refreshIFirmAccessibleContacts(true);
            }, 3600000);
            await this.refreshIFirmAccessibleContacts();
        } else {
            clearInterval(this.ifirmContactAccessibleRefresh);
            this.ifirmContactAccessibleRefresh = undefined;
        }
    }

    private async refreshIFirmAccessibleContacts(forceReload = false) {
        if (!this.authService.inReloginPrompted) {
            await this.iFirmContactsService.getAccessibleContacts(forceReload);
            this.filtersChangedEmitter.next(false);
        } else if (!this.accessibleSessionChangedSub) {
            const sessionChangedSub = this.authService.sessionChangedReceivedEvent$.subscribe(async () => {
                this.subscription.remove(sessionChangedSub);
                sessionChangedSub.unsubscribe();
                this.accessibleSessionChangedSub = null;
                await this.refreshIFirmAccessibleContacts(forceReload);
            });
            this.accessibleSessionChangedSub = sessionChangedSub;
            this.subscription.add(sessionChangedSub);
        }
    }

    /**
     * Handles changes to the filters, optionally tracks the analytics.
     * Bursts of calls arriving within `FILTER_CHANGE_DEBOUNCE_MS` (e.g. cascading async effects) are
     * coalesced via throttleTime: the first call notifies immediately, the last one in the burst notifies
     * again once things go quiet. Analytics tracking is never dropped, even when the notification is coalesced.
     * @param trackAnalytics - If `true`, analytics for the filter change will be tracked.
     */
    handleFilterChange(trackAnalytics = false) {
        if (this.skipFilterChange.Filtertype !== "Roles" || (this.skipFilterChange.Filtertype === "Roles" && this.skipFilterChange.value)) {
            this.skipFilterChange.Filtertype = "all";
            this.skipFilterChange.value = false;
            this.filterChangeRequestEmitter.next(trackAnalytics);
        }
    }

    filterCards(cards: IBaseCard[], statusIds?: string[]): IBaseCard[] {
        const filters = Array.from(this.activeFilters.values());
        return this.filterCardsDisplayable(cards, statusIds).filter((card) => filters.every((f) => f(card)));
    }

    sortCards(cards: IBaseCard[]) {
        const activeSort = this.activeSort;
        if (activeSort) {
            cards.sort((a, b) => activeSort(a, b));
        }
    }

    filterCardsDisplayable(cards: IBaseCard[], statusIds?: string[]): IBaseCard[] {
        return cards.filter(
            (card) =>
                (!statusIds || statusIds.includes(this.cardDisplayService.getCardDisplayStatusId(card))) && this.isCardAvailableForApplication(card),
        );
    }

    isCardAvailableForApplication(card: IBaseCard) {
        if (isiFirmTax(this.currentDashboard?.ApplicationType)) {
            return !this.deleteCardByGrouping(card) && this.ifirmTaxCardsService.isCardAvailable(card.cardId);
        } else if (isJobsBilling(this.currentDashboard?.ApplicationType)) {
            const jobCard = card as JobsBillingCard;
            return (
                jobCard.contactInformation.iFirmContactId &&
                this.iFirmContactsService.isContactAccessible(Number.parseInt(jobCard.contactInformation.iFirmContactId, 10)) &&
                this.jobsAndBillingCommonService.hasAccess("view_job", jobCard)
            );
        } else if (isTaxprep(this.currentDashboard?.ApplicationType)) {
            return !this.deleteCardByGrouping(card);
        } else {
            return true;
        }
    }

    deleteCardByGrouping(cardData) {
        if (cardData?.return && cardData.return.type !== "Taxpayer") {
            // Card type is not taxpayer
            if (cardData.isSplit !== null) {
                return !cardData.isSplit;
            } else {
                return this.currentDashboard?.DisplayGroupedCards;
            }
        }
        return false;
    }

    addFilter(filterName: string, filter: (card: IBaseCard) => boolean, desc: string, selectedValues: string) {
        this.activeFilters.set(filterName, filter);
    }

    removeFilter(filterName: string) {
        this.activeFilters.delete(filterName);
    }

    private trackFilterAnalytics() {
        const item = localStorage.getItem(`Filter_Selection_${this.currentDashboard?.Id}`);
        const storedSelection = item && item !== "" ? JSON.parse(item) : {};

        if (storedSelection && Object.hasOwn(storedSelection, "SortOrder")) {
            delete storedSelection.SortOrder;
        }
        if (storedSelection && Object.hasOwn(storedSelection, "SortBy")) {
            delete storedSelection.SortBy;
        }
        this.analyticsService.track(FiltersService.FILTER_ANALYTICS_EVENT, { Filters: storedSelection });
    }
}
